import { ITab, ISaveModel } from './types';
import { IWorkspace, IWorkspaceItem, MapOptions } from '../../store/workspace/types';
import { Dispatch } from 'react';
import { api } from '../root';
import { createSaveModel } from './processors';
import { TreeViewDataItem, ICategorizeOptions, IFilterOptions } from 'store/sidebar/types';
import { handleRequestErrors } from 'helpers/functions/commons';
import { createRequestModel } from 'api/mapView/processors';
import { addNotification } from 'store/notifications/slice';



// Helper function to process workspace and extract current user's access level
// This function works with any workspace type and preserves the original type
export const processWorkspaceAccessLevel = <T extends { isOwner?: boolean; sharedWith?: any[] }>(
    workspace: T, 
    currentUserId?: string
): T & { accessLevel?: 'read' | 'write'; isOwner: boolean } => {
    // Determine if user is the owner
    // Owner can be determined by: explicit isOwner flag OR not being in sharedWith array
    const userIsOwner = workspace.isOwner === true;
    
    // If user is the owner, they have full access
    if (userIsOwner) {
        return {
            ...workspace,
            isOwner: true,
            accessLevel: undefined // Owner doesn't need accessLevel
        };
    }
    
    // Extract access level from sharedWith array
    const sharedWithEntry = workspace.sharedWith?.find((user: any) => user.userId === currentUserId || user.id === currentUserId);
    
    // If user is in sharedWith, use that access level; otherwise they might be owner (no isOwner flag in response)
    const accessLevel = sharedWithEntry?.accessLevel as 'read' | 'write' | undefined;
    
    // If not in sharedWith and isOwner is not explicitly false, they're likely the owner
    const isOwner = workspace.isOwner !== false && !sharedWithEntry;
    
    return {
        ...workspace,
        isOwner,
        accessLevel: isOwner ? undefined : accessLevel,
        // Normalize sharedWith to use consistent structure
        sharedWith: workspace.sharedWith?.map((user: any) => ({
            id: user.userId || user.id,
            name: user.name || '',
            accessLevel: user.accessLevel
        })) || []
    };
};

export const getAllWorkspaces = async (thunkAPI: unknown) => {
    try {
        const response = await api.get<{workspaces: IWorkspaceItem[]}>('/Workspace/GetAll');
        return response.data
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> }
        thunkRejectError.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError.rejectWithValue(validationMessage);
    }
} 

export const R_addWorkspace = async (data: { title: string, unit: number, datasetIds: string[], sharedWith: string[] }) => {
    const response = await api.post('/Workspace', data);
    return response.data;
}

export const R_copyWorkspace = async (data: { sourceWorkspaceId: string, title: string }) => {
    const response = await api.post(`/Workspace/${data.sourceWorkspaceId}/copy`, { title: data.title });
    return response.data;
}

export const R_editWorkspace = async (data: Partial<IWorkspaceItem>) => {
    const response = await api.patch(`/Workspace/${data.id}`, data)
    return response.data
}

export const R_editWorkspaceFilters = async (data: {id: string, title?: string, unit: number | null, mapOptions?: TreeViewDataItem, filterOptions?: IFilterOptions, categorizeOptions?: ICategorizeOptions}) => {
    const response = await api.patch(`/Workspace/${data.id}`, {
        title: data.title,
        unit: data.unit,
        mapOptions: data.mapOptions,
        filterOptions: {
            ...data.filterOptions,
            filters: data.filterOptions?.filters,
        },
        categorizeOptions: data.categorizeOptions
    });
    return response.data;
}

// Dedicated, read-only-safe endpoint for filter option changes (enable/disable, clearing
// filters, apply toggles). Persists only the FilterOptionsModel, not the whole workspace.
export const R_editFilterOptions = async (data: {id: string, filterOptions: IFilterOptions}) => {
    const response = await api.patch(`/Workspace/${data.id}/FilterOptions`, {
        isEnabled: data.filterOptions.isEnabled,
        applyToUserData: data.filterOptions.applyToUserData,
        applyToProductionData: data.filterOptions.applyToProductionData,
        applyToBenchmarkData: data.filterOptions.applyToBenchmarkData,
        filters: data.filterOptions.filters,
    });
    return response.data;
}

export const R_editAnalogueWorkspaceFilters = async (data: {id: string, title?: string, unit: number | null, mapOptions?: TreeViewDataItem, filterOptions?: IFilterOptions, categorizeOptions?: ICategorizeOptions}) => {
    const response = await api.patch(`/Workspace/AnalogeFilterWorkspace/${data.id}`, {
        title: data.title,
        unit: data.unit,
        mapOptions: data.mapOptions,
        filterOptions: {
            ...data.filterOptions,
            filters: data.filterOptions?.filters,
        },
        categorizeOptions: data.categorizeOptions
    });
    return response.data;
}

export const R_deleteWorkspace = async (data: {id: string}) => {
    const response = await api.delete(`/Workspace/${data.id}`)
    return response.data
}

export const getWorkspaceById = async (id: string) => {
    const response = await api.get<IWorkspace>(`/Workspace/${id}`);
    return {workspace: response.data};
}

export const savePlotOptions = async (data: {plotOptions: TreeViewDataItem | undefined, selectedTab: ITab | undefined}, thunkAPI: unknown) => {
    try {
        const saveData: ISaveModel = createSaveModel(data);
        const response = await api.put<IWorkspace>(`/Workspace/${data.selectedTab?.workspaceId}/CrossPlot/${data.selectedTab?.id}`, saveData);
        return response.data;
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> }
        // thunkRejectError.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError.rejectWithValue(validationMessage);
    }
}

export const saveMapOptions = async (data: {id: string, mapOptions: MapOptions}) => {
    const response = await api.patch(`/Workspace/${data.id}`, { mapViewOptions: data.mapOptions })
    return response.data
}
